import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import Avatar from '../../components/common/Avatar';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { 
  Users, 
  Plus, 
  Search, 
  MoreHorizontal, 
  Mail, 
  Phone,
  Pencil,
  Trash2,
  Send,
  Loader2,
  UserCog,
  Briefcase,
  Clock,
  ArrowRightLeft,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const UsersPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  
  // Transfer dialog state
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferFrom, setTransferFrom] = useState('');
  const [transferTo, setTransferTo] = useState('');
  const [transferOptions, setTransferOptions] = useState({
    projects: true,
    tasks: true,
    appointments: true,
    contracts: false
  });
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleTransferContent = async () => {
    if (!transferFrom || !transferTo || transferFrom === transferTo) {
      toast.error('Sélectionnez deux membres différents');
      return;
    }
    
    setTransferring(true);
    try {
      const response = await api.post('/users/transfer-member-content', {
        from_member_id: transferFrom,
        to_member_id: transferTo,
        transfer_projects: transferOptions.projects,
        transfer_tasks: transferOptions.tasks,
        transfer_appointments: transferOptions.appointments,
        transfer_contracts: transferOptions.contracts
      });
      
      toast.success(response.data.message);
      setShowTransferDialog(false);
      setTransferFrom('');
      setTransferTo('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du transfert');
    } finally {
      setTransferring(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Get team members (not clients or affiliates)
      const response = await api.get('/users/team-members');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des membres');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCredentials = async (userId, email) => {
    try {
      await api.post(`/users/${userId}/send-credentials`);
      toast.success(`Identifiants envoyés à ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handleDeleteUser = async (userId, name) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${name} ?\n\nCette action est irréversible.`)) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Membre supprimé');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      'agency': { label: 'Agence', className: 'bg-purple-100 text-purple-700' },
      'project_manager': { label: 'Chef de projet', className: 'bg-blue-100 text-blue-700' },
      'team_member': { label: 'Membre', className: 'bg-cyan-100 text-cyan-700' },
      'creative': { label: 'Créatif', className: 'bg-pink-100 text-pink-700' },
      'customer_service': { label: 'Service client', className: 'bg-amber-100 text-amber-700' },
    };
    const config = roleConfig[role] || { label: role, className: 'bg-slate-100 text-slate-700' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      <main className={cn(
        "transition-all duration-300 px-4 md:px-6 pb-6",
        collapsed ? "md:ml-20" : "md:ml-64"
      )} style={{ paddingTop: "130px" }}>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Membres de l&apos;équipe
            </h1>
            <p className="text-slate-500">Gérez les membres de votre agence</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowTransferDialog(true)}
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Transférer contenu
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => navigate('/users/create')}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau membre
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher un membre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant={filterRole === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterRole('all')}
                >
                  Tous
                </Button>
                <Button
                  variant={filterRole === 'project_manager' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterRole('project_manager')}
                >
                  Chefs de projet
                </Button>
                <Button
                  variant={filterRole === 'team_member' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterRole('team_member')}
                >
                  Membres
                </Button>
                <Button
                  variant={filterRole === 'creative' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterRole('creative')}
                >
                  Créatifs
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Liste des membres ({filteredUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Aucun membre trouvé</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Membre</TableHead>
                    <TableHead>Rôle</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div 
                          className="flex items-center gap-3 cursor-pointer hover:bg-slate-100 rounded-lg p-1 -m-1 transition-colors"
                          onClick={() => navigate(`/users/${user.id}/edit`)}
                          data-testid={`member-row-${user.id}`}
                        >
                          <Avatar name={`${user.first_name} ${user.last_name}`} size="sm" />
                          <div>
                            <p className="font-medium text-slate-800 hover:text-blue-600 hover:underline">
                              {user.first_name} {user.last_name}
                            </p>
                            <p className="text-xs text-slate-500">{user.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getRoleBadge(user.role)}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {user.phone && (
                            <p className="text-sm text-slate-600 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {user.phone}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={user.is_active !== false ? 'text-green-600' : 'text-red-600'}>
                          {user.is_active !== false ? 'Actif' : 'Inactif'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/users/${user.id}/edit`)}>
                              <Pencil className="w-4 h-4 mr-2" /> Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSendCredentials(user.id, user.email)}>
                              <Send className="w-4 h-4 mr-2" /> Renvoyer identifiants
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600 focus:text-red-600"
                              onClick={() => handleDeleteUser(user.id, `${user.first_name} ${user.last_name}`)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Transfer Content Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5" />
              Transférer le contenu d&apos;un membre
            </DialogTitle>
            <DialogDescription>
              Transférez les projets, tâches et RDV d&apos;un membre sortant vers un autre membre de l&apos;équipe.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
              <p className="text-sm text-amber-700">
                Cette action est irréversible. Vérifiez bien les membres sélectionnés avant de confirmer.
              </p>
            </div>
            
            <div>
              <Label>Membre source (sortant)</Label>
              <Select value={transferFrom} onValueChange={setTransferFrom}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionner le membre sortant" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id} disabled={u.id === transferTo}>
                      {u.first_name} {u.last_name} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-center">
              <ArrowRightLeft className="w-5 h-5 text-slate-400" />
            </div>
            
            <div>
              <Label>Membre cible (reprend le contenu)</Label>
              <Select value={transferTo} onValueChange={setTransferTo}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionner le nouveau responsable" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id} disabled={u.id === transferFrom}>
                      {u.first_name} {u.last_name} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-3 pt-2">
              <Label>Éléments à transférer</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="transfer-projects"
                    checked={transferOptions.projects}
                    onCheckedChange={(checked) => setTransferOptions(prev => ({ ...prev, projects: checked }))}
                  />
                  <label htmlFor="transfer-projects" className="text-sm">Projets assignés</label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="transfer-tasks"
                    checked={transferOptions.tasks}
                    onCheckedChange={(checked) => setTransferOptions(prev => ({ ...prev, tasks: checked }))}
                  />
                  <label htmlFor="transfer-tasks" className="text-sm">Tâches assignées</label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="transfer-appointments"
                    checked={transferOptions.appointments}
                    onCheckedChange={(checked) => setTransferOptions(prev => ({ ...prev, appointments: checked }))}
                  />
                  <label htmlFor="transfer-appointments" className="text-sm">Rendez-vous</label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="transfer-contracts"
                    checked={transferOptions.contracts}
                    onCheckedChange={(checked) => setTransferOptions(prev => ({ ...prev, contracts: checked }))}
                  />
                  <label htmlFor="transfer-contracts" className="text-sm text-amber-600">Contrats actifs (attention)</label>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleTransferContent}
              disabled={!transferFrom || !transferTo || transferFrom === transferTo || transferring}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {transferring ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRightLeft className="w-4 h-4 mr-2" />
              )}
              Transférer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
